import { CommonModule } from '@angular/common'
import { Component, inject, Input, TemplateRef, ViewChild } from '@angular/core'
import { E11ModalGlobalService } from '@engineering11/ui-lib/e11-modal'
import { TranslateModule } from '@ngx-translate/core'

export type UpgradePromptVariant = 'inline-banner' | 'modal-link'

/**
 * Reusable Cnect Plus upgrade affordance. `inline-banner` renders a locked-feature box in place;
 * `modal-link` renders a subtle link that opens an explanatory modal. Rich copy can be projected
 * (default slot for the banner, `[upgradeModalBody]` slot for the modal) or supplied via `body`.
 * CTA is contact-CS/Admin only — there is no self-serve checkout.
 */
@Component({
  selector: 'upgrade-prompt',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  template: `
    <!-- inline-banner -->
    <div
      *ngIf="variant === 'inline-banner'"
      class="e11-flex e11-items-start e11-gap-2 e11-rounded-md e11-bg-skin-app-bg e11-border e11-border-skin-app-borders e11-px-3 e11-py-2"
    >
      <span class="material-icons-outlined md-18 e11-text-skin-light e11-shrink-0">{{ icon }}</span>
      <div class="e11-flex e11-flex-col e11-gap-1">
        <p *ngIf="headline" class="e11-text-xs e11-font-primary-medium e11-text-skin-dark">{{ headline | translate }}</p>
        <div class="e11-text-xs e11-text-skin-dark">
          <ng-container *ngIf="body; else projectedBanner">{{ body | translate }}</ng-container>
          <ng-template #projectedBanner><ng-content></ng-content></ng-template>
        </div>
        <p *ngIf="ctaText" class="e11-text-xs e11-text-skin-light">{{ ctaText | translate }}</p>
      </div>
    </div>

    <!-- modal-link -->
    <ng-container *ngIf="variant === 'modal-link'">
      <div
        class="e11-text-skin-primary-accent hover:e11-text-skin-primary e11-flex e11-items-center e11-cursor-pointer e11-w-fit"
        (click)="openModal()"
      >
        <span class="material-icons md-18 e11-mr-2">{{ icon }}</span>
        <span class="e11-text-sm">{{ linkText || defaultLinkText | translate }}</span>
      </div>
      <ng-template #modalBody>
        <div class="e11-text-skin-primary-accent e11-flex e11-gap-2 e11-items-center e11-mb-4">
          <span class="material-icons md-24">{{ icon }}</span>
          <h3>{{ headline || linkText || defaultLinkText | translate }}</h3>
        </div>
        <div class="2xl:e11-max-w-screen-md">
          <ng-container *ngIf="body; else projectedModal">
            <p>{{ body | translate }}</p>
          </ng-container>
          <ng-template #projectedModal><ng-content select="[upgradeModalBody]"></ng-content></ng-template>
          <p *ngIf="ctaText">{{ ctaText | translate }}</p>
        </div>
      </ng-template>
    </ng-container>
  `,
})
export class UpgradePromptComponent {
  @Input() variant: UpgradePromptVariant = 'inline-banner'
  @Input({ required: true }) featureName!: string
  @Input() headline?: string
  @Input() body?: string
  @Input() icon = 'lock'
  @Input() linkText?: string
  @Input() ctaText? = 'To learn more and change your plan, contact your account Administrator or a Cnect Customer Success representative.'

  @ViewChild('modalBody') modalBody!: TemplateRef<any>
  #modal = inject(E11ModalGlobalService)

  get defaultLinkText(): string {
    return 'Why am I unable to use this?'
  }

  openModal(): void {
    this.#modal.openModal({ title: this.featureName, body: this.modalBody, width: 'lg' })
  }
}
